import {Rectangle, type RectangleProps} from 'recharts';

import {isNotNil} from 'ramda';

interface ChartStackedBarShapeProps extends RectangleProps {
  /** Data keys of the stacked bars, in the order that the chart renders them. */
  stackKeys: string[];
  /** Set by Recharts: the data key of the bar that the segment belongs to. */
  dataKey?: unknown;
  /** Set by Recharts: the data row of the segment. */
  payload?: unknown;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && isNotNil(value);

// Recharts types the data row as unknown. A missing or unknown value draws no segment.
function valueOf(row: unknown, key: unknown) {
  if (!isRecord(row) || typeof key !== 'string') {
    return 0;
  }
  const value = row[key];
  return typeof value === 'number' ? value : 0;
}

/**
 * Segment of a stacked bar that rounds only the outer end of its stack. Pass it as
 * `shape={<ChartStackedBarShape stackKeys={keys} />}` to every `Bar` of the stack, together with
 * the `radius` of the chart style. A radius on the top series alone leaves the stack square in
 * every month where that series is 0.
 */
export function ChartStackedBarShape(props: ChartStackedBarShapeProps) {
  const value = valueOf(props.payload, props.dataKey);
  const laterValues = props.stackKeys
    .slice(props.stackKeys.indexOf(String(props.dataKey)) + 1)
    .map((key) => valueOf(props.payload, key));
  // Positive values stack up from 0 and negative values stack down, so each side has its own end.
  const isOuterEnd =
    value > 0
      ? laterValues.every((laterValue) => laterValue <= 0)
      : laterValues.every((laterValue) => laterValue >= 0);

  return <Rectangle {...props} radius={isOuterEnd ? props.radius : 0} />;
}
